import React, { useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useThemeContext } from '../../context/ThemeContext';
import { useData } from '../../context/DataContext';
import { Sun, Moon, Phone, Monitor, Globe } from '../../icons';

const TopBar: React.FC = () => {
    const { isDarkMode, toggleTheme } = useThemeContext();
    const { generalInfo } = useData();

    const adjustFontSize = useCallback((action: 'increase' | 'decrease' | 'reset') => {
        const html = document.documentElement;
        const currentSize = parseFloat(getComputedStyle(html).fontSize);

        switch (action) {
            case 'increase':
                if (currentSize < 24) {
                    html.style.fontSize = `${currentSize + 2}px`;
                }
                break;
            case 'decrease':
                if (currentSize > 12) {
                    html.style.fontSize = `${currentSize - 2}px`;
                }
                break;
            case 'reset':
                html.style.fontSize = '16px';
                break;
        }
    }, []);

    return (
        <div
            className={`hidden md:flex justify-between items-center py-1 px-4 text-xs font-medium z-50 relative ${isDarkMode ? 'bg-blue-900 text-white' : 'bg-blue-800 text-white'
                }`}
            role="complementary"
            aria-label="Accessibility toolbar"
        >
            <div className="flex items-center space-x-6">
                {generalInfo?.helpline_number && (
                    <div className="flex items-center gap-2">
                        <Phone className="w-3 h-3" aria-hidden="true" />
                        <span>Helpline: {generalInfo.helpline_number}</span>
                    </div>
                )}
                <div className="hidden lg:flex items-center gap-2">
                    <span>Mon-Fri 9:00 AM - 5:00 PM</span>
                </div>
            </div>

            <div className="flex items-center space-x-4 divide-x divide-white/20">
                <div className="flex items-center space-x-3 pl-4" role="group" aria-label="Font size controls">
                    <span className="uppercase tracking-wider opacity-80">Accessibility:</span>
                    <button
                        onClick={() => adjustFontSize('decrease')}
                        className="hover:text-yellow-300 transition-colors px-1"
                        title="Decrease Font Size"
                        aria-label="Decrease font size"
                    >
                        A-
                    </button>
                    <button
                        onClick={() => adjustFontSize('reset')}
                        className="hover:text-yellow-300 transition-colors px-1"
                        title="Reset Font Size"
                        aria-label="Reset font size to default"
                    >
                        A
                    </button>
                    <button
                        onClick={() => adjustFontSize('increase')}
                        className="hover:text-yellow-300 transition-colors px-1"
                        title="Increase Font Size"
                        aria-label="Increase font size"
                    >
                        A+
                    </button>
                </div>

                <div className="flex items-center space-x-3 pl-4">
                    <Link
                        to="/accessibility"
                        className="flex items-center gap-1 hover:text-yellow-300 transition-colors"
                        title="Screen Reader Access and Accessibility Information"
                    >
                        <Monitor className="w-3 h-3" aria-hidden="true" />
                        <span>Screen Reader</span>
                    </Link>
                </div>

                <div className="flex items-center space-x-3 pl-4">
                    <button
                        onClick={toggleTheme}
                        className="flex items-center gap-1 hover:text-yellow-300 transition-colors"
                        aria-label={isDarkMode ? 'Switch to light theme' : 'Switch to dark theme'}
                        aria-pressed={isDarkMode}
                    >
                        {isDarkMode ? <Sun className="w-3 h-3" aria-hidden="true" /> : <Moon className="w-3 h-3" aria-hidden="true" />}
                        <span>{isDarkMode ? 'Light' : 'Dark'}</span>
                    </button>
                </div>

                <div className="flex items-center space-x-3 pl-4">
                    <button
                        className="flex items-center gap-1 hover:text-yellow-300 transition-colors"
                        aria-label="Toggle language between Hindi and English"
                    >
                        <Globe className="w-3 h-3" aria-hidden="true" />
                        <span>Hindi / English</span>
                    </button>
                </div>
            </div>
        </div>
    );
};

export default TopBar;

